// Layer 4 (UI): polish.P9 (#657) — the metal's path as the page's strip and tab
// bar (locked comp `metal-accounting.html` A, `.strip`/`.stage`), in the
// Advisory strip idiom (DESIGN.md § Strip and stepper): caps stage name,
// primary figure, comparison lines, actor chip and state pill pinned to the
// foot; hairline cells with a rotated notch at each boundary; the selected cell
// takes the accent-selected wash and a 3px crimson bottom rule. The Closure cell
// ends the strip on the ok wash when the balance closes, as Downtime's year
// cell does. Renders `balanceStripCells` verbatim.

import { GlassCard, Pill } from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { vizToneColor, type VizTone } from '@/versions/v1/modules/_viz';
import { ActorChip } from '@/versions/v1/modules/advisory/PipelineStrip';
import type { BalanceStageId, BalanceStripCell, StripRun } from './BalanceStrip.transform';

const PILL_TONE: Record<VizTone, 'danger' | 'warning' | 'success' | 'default'> = {
  bad: 'danger',
  warn: 'warning',
  ok: 'success',
  neutral: 'default',
  info: 'default',
};

export const STATE_PILL = 'whitespace-nowrap px-1.5 py-0 text-[10px] font-semibold leading-4 tracking-[0.05em]';

function Runs({ runs }: { runs: readonly StripRun[] }) {
  return (
    <>
      {runs.map((run, index) => (
        <span
          key={index}
          className={cn(run.strong ? 'font-semibold text-ink-secondary' : undefined, run.nowrap ? 'whitespace-nowrap' : undefined)}
          style={run.tone && run.tone !== 'neutral' ? { color: vizToneColor(run.tone) } : undefined}
        >
          {run.text}
        </span>
      ))}
    </>
  );
}

export function BalanceStrip({
  cells,
  selected,
  onSelect,
}: {
  cells: readonly BalanceStripCell[];
  selected: BalanceStageId;
  onSelect: (stage: BalanceStageId) => void;
}) {
  return (
    <Commentable name="BalanceStrip">
      <GlassCard
        as="nav"
        tone="matte"
        aria-label="Metal path"
        className="overflow-hidden rounded-2xl border-[var(--hairline-strong)] p-0"
      >
        <div role="tablist" aria-label="Stages" className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5">
          {cells.map((cell) => {
            const active = cell.id === selected;
            const face = active
              ? cell.closes
                ? 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,color-mix(in_srgb,var(--state-online)_10%,var(--surface-2)))]'
                : 'bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]'
              : cell.closes
                ? 'bg-[color-mix(in_srgb,var(--state-online)_10%,var(--surface-2))] hover:bg-[color-mix(in_srgb,var(--state-online)_14%,var(--surface-2))]'
                : 'bg-[var(--surface-2)] hover:bg-[var(--surface-1)] after:bg-[var(--surface-2)] hover:after:bg-[var(--surface-1)]';
            return (
              <button
                key={cell.id}
                id={`balance-stage-${cell.id}`}
                type="button"
                role="tab"
                aria-selected={active}
                aria-controls="balance-stage-panel"
                data-balance-stage={cell.id}
                onClick={() => onSelect(cell.id)}
                className={cn(
                  'relative flex min-h-[7.5rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] py-3 pl-4 pr-3.5 text-left transition-colors last:border-r-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[var(--color-accent-500)] xl:border-b-0',
                  cell.closes
                    ? ''
                    : "after:pointer-events-none after:absolute after:right-[-6px] after:top-1/2 after:z-10 after:hidden after:h-[10px] after:w-[10px] after:-translate-y-1/2 after:rotate-45 after:border-r after:border-t after:border-[var(--hairline-strong)] after:content-[''] last:after:hidden xl:after:block xl:last:after:hidden",
                  active ? 'shadow-[inset_0_-3px_0_var(--color-accent-500)] after:bg-[color-mix(in_srgb,var(--color-accent-500)_8%,var(--surface-2))]' : '',
                  face,
                )}
              >
                <span className="text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.name}</span>
                <span
                  data-balance-figure={cell.id}
                  className="flex min-w-0 items-baseline gap-1.5 whitespace-nowrap text-2xl font-semibold leading-none tracking-[-0.01em] tabular-nums text-ink-primary"
                >
                  {cell.figure}
                  {cell.unit ? <span className="text-sm font-semibold tracking-normal text-ink-secondary">{cell.unit}</span> : null}
                </span>
                {cell.lines.map((line, index) => (
                  <span
                    key={index}
                    data-balance-line={`${cell.id}-${index}`}
                    className="text-xs leading-snug tabular-nums text-muted"
                  >
                    <Runs runs={line} />
                  </span>
                ))}
                <span className="mt-auto flex min-w-0 flex-wrap items-center gap-1 pt-1">
                  <ActorChip actor={cell.actor.kind}>
                    {/* The glyph already marks the actor; the named lead drops below 1400 px so the chip never truncates. */}
                    {cell.actor.lead ? <span className="max-[1399px]:hidden">{cell.actor.lead} · </span> : null}
                    {cell.actor.label}
                  </ActorChip>
                  {cell.pill ? (
                    <Pill tone={PILL_TONE[cell.pill.tone]} className={STATE_PILL}>
                      {cell.pill.label}
                    </Pill>
                  ) : null}
                </span>
              </button>
            );
          })}
        </div>
      </GlassCard>
    </Commentable>
  );
}
